import * as React from 'react';
import CustomSelect from './custom-select';
import CustomOption from './custom-options';
import CustomOptionsGroup from './custom-options-group';
import './index.scss';
import clsx from 'clsx';

interface DropDownContextProps {
  selectValue?: string | number | string[] | number[];
  multiple?: boolean;
}

type Value = any; //string | number | string[] | number[]; /* edited since filter in table was not accepting array */

export const DropDownContext = React.createContext<DropDownContextProps>({});

export interface Options {
  label?: React.ReactNode | string;
  value: any;
  disabled?: boolean;
  children?: React.ReactNode;
}
interface Validation {
  message: string;
}

export interface DropdownProps {
  disabled?: boolean;
  value?: Value;
  options?: Options[];
  onSelect?: (value: Value) => void;
  customOnSelect?: (value: Value) => void;
  multiple?: boolean;
  label?: React.ReactNode;
  customSelectValue?: string;
  clearAll?: boolean;
  children?: React.ReactNode;
  styles?: React.CSSProperties;
  className?: string;
  size?: 'small' | 'medium';
  placeholder?: string;
  popperContainer?: () => Element;
  required?: boolean;
  validation?: Validation;
}

const Dropdown = React.forwardRef<HTMLSelectElement, DropdownProps>(function Dropdown(
  { children, options, label, clearAll, className, styles, size = 'medium', required = false, validation, ...props },
  ref
) {
  const childrenArray = React.Children.toArray(children);
  clearAll &&
    childrenArray.unshift(
      <option value="apac_komodo_dropdown_clear">
        <span className="icon-24-refresh-mono" /> Clear
      </option>
    );

  const renderOption = (option: Options, index) => {
    return (
      <CustomOption
        key={JSON.stringify(option?.value || '') || index}
        value={option?.value}
        disabled={option?.disabled}
      >
        {option?.children || option?.label}
      </CustomOption>
    );
  };

  const optionItems = (childrenArray: React.ReactNode[]) => {
    return childrenArray?.map((child, index) => {
      if (!React.isValidElement(child)) {
        return null;
      }

      if (child.type === 'optgroup') {
        return (
          <CustomOptionsGroup {...child.props} key={index}>
            {optionItems(React.Children.toArray(child.props.children))}
          </CustomOptionsGroup>
        );
      }

      if (child.type === 'option') {
        return renderOption(child.props, index);
      }
    });
  };

  const renderOptionField = () => {
    if (Array.isArray(options)) {
      const cloneOptions = [...options];
      clearAll &&
        cloneOptions.unshift({
          label: (
            <>
              <span className="icon-24-refresh-mono" /> Clear
            </>
          ),
          value: 'apac_komodo_dropdown_clear',
        });
      return cloneOptions?.map((option, index) => renderOption(option, index));
    }
  };

  const DropdownCls = clsx({
    'c-dropdown c-dropdown--wrap': true,
    [className]: !!className,
    'c-dropdown__small_wrap': size === 'small',
  });

  return (
    <div className={DropdownCls} style={styles}>
      {label &&
        React.cloneElement(
          <label className="c-input-label" style={{ fontWeight: 500 }}>
            {label}
          </label>,
          { required }
        )}

      <CustomSelect {...props} size={size} ref={ref} validation={validation}>
        {options ? renderOptionField() : optionItems(childrenArray)}
      </CustomSelect>

      {validation?.message && (
        <div className="c-input-validation c-input-validation--error">
          <span className="c-input__validation-icon"></span>
          <span className="c-input__validation-message">{validation?.message}</span>
        </div>
      )}
    </div>
  );
});

export { Dropdown };
